import { Component, AfterViewInit, ElementRef, ViewChild, Input } from '@angular/core';

@Component({
	selector: 'app-chart',
	template: `<canvas #chartCanvas></canvas>`,
	styles: [`
    canvas {
      width: 100%;
      display: block;
    }
  `]
})
export class LineChartComponent implements AfterViewInit {
	@ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

	@Input() months: any;
	@Input() datasets: any;

	// private months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
	// private datasets = [
	//   { label: 'Total Earnings', data: [10000, 8000, 8500, 14000, 27000, 25000, 11000], color: '#1AACD9' },
	//   { label: 'Clients', data: [2500, 4000, 5000, 6000, 7000, 8000, 10000], color: '#EB1461' },
	//   { label: 'Orders', data: [6000, 9000, 12000, 16000, 12000, 14000, 30000], color: '#FCCF0B' },
	//   { label: 'Total Expenses', data: [800, 2200, 2500, 2200, 2000, 2000, 4500], color: '#18F121' },
	// ];

	private yAxisLeftLabels: string[] = [];
	private yAxisRightLabels: string[] = [];

	getFormattedYAxisLabels(): string[] {
		return this.yAxisLeftLabels.map(label => label.toLocaleString());
	}

	ngOnInit() {
	}

	ngAfterViewInit() {
		this.drawChart();
	}

	private drawChart() {

		const getYAxisLabels = (maxValue) => {
			if (maxValue <= 0) return ["0"]; // Handle edge case

			// Calculate a dynamic step size
			const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
			let step = magnitude / 2; // Set step to half of the magnitude for a good distribution

			// Round maxValue to the nearest step
			const roundedMax = Math.ceil(maxValue / step) * step;

			// Generate labels from 0 to roundedMax and format with commas
			return Array.from({ length: roundedMax / step + 1 }, (_, i) =>
				(i * step).toLocaleString() // Format number with commas
			);
		};

		// Find the largest number in this.datasets
		const largestNumber = Math.max(
			...this.datasets.map(dataset => Math.max(...dataset.data))
		);

		// Generate yAxisLeftLabels dynamically
		this.yAxisLeftLabels = getYAxisLabels(largestNumber);
		this.yAxisRightLabels = getYAxisLabels(largestNumber);

		const canvas = this.chartCanvas.nativeElement;
		const ctx = canvas.getContext('2d')!;
		canvas.width = canvas.parentElement!.clientWidth;
		canvas.height = 600;

		const paddingLeft = 60;
		const paddingRight = 60;
		const paddingTop = 40;
		const paddingBottom = 80;
		const width = canvas.width - paddingLeft - paddingRight;
		const height = canvas.height - paddingTop - paddingBottom;

		const maxYValue = largestNumber * 1.15;
		const minYValue = -(largestNumber * 0.15);
		const yScale = height / (maxYValue - minYValue);

		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.strokeStyle = '#EDEDED';
		ctx.lineWidth = 1;

		const verticalLinesX: number[] = [];
		for (let i = 0; i <= this.months.length; i++) {
			const x = paddingLeft + (i / this.months.length) * width;
			verticalLinesX.push(x);

			ctx.beginPath();
			ctx.moveTo(x, paddingTop);
			ctx.lineTo(x, height + paddingTop);
			ctx.stroke();

			if (i < this.months.length) {
				ctx.fillStyle = '#393939';
				ctx.font = '14px Open Sans';
				ctx.textAlign = 'center';
				ctx.fillText(this.months[i], x, height + paddingTop + 25);
			}
		}

		// draw left Y labels — smaller font and skip labels that are too close to avoid overlap
		ctx.fillStyle = '#393939';
		ctx.font = '10px Open Sans';
		ctx.textAlign = 'right';
		ctx.textBaseline = 'middle';
		let prevYLeft: number | null = null;
		const minLabelSpacing = 14; // px

		this.yAxisLeftLabels.forEach(label => {
			const numericLabel = parseInt(label.toString().replace(/,/g, ''), 10);
			const y = height + paddingTop - (numericLabel - minYValue) * yScale;
			
			if (prevYLeft !== null && Math.abs(prevYLeft - y) < minLabelSpacing) {
				// skip this label because it's too close to the previous one
				return;
			}

			ctx.beginPath();
			ctx.moveTo(paddingLeft, y);
			ctx.lineTo(width + paddingLeft, y);
			ctx.stroke();
			ctx.fillText(label.toString(), paddingLeft - 14, y);
			prevYLeft = y;
		});

		// draw right Y labels — smaller font and skip labels that are too close to avoid overlap
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
		let prevYRight: number | null = null;

		this.yAxisRightLabels.forEach(label => {
			const numericLabel = parseInt(label.toString().replace(/,/g, ''), 10);
			const y = height + paddingTop - (numericLabel - minYValue) * yScale;
			if (prevYRight !== null && Math.abs(prevYRight - y) < minLabelSpacing) {
				return;
			}
			ctx.fillStyle = '#393939';
			ctx.fillText(label.toString(), width + paddingLeft + 14, y);
			prevYRight = y;
		});

		this.datasets.forEach(dataset => {
			ctx.beginPath();
			ctx.strokeStyle = dataset.color;
			ctx.lineWidth = 2;
			ctx.lineJoin = 'round';

			const points = dataset.data.map((value, i) => ({
				x: verticalLinesX[i],
				y: height + paddingTop - (value - minYValue) * yScale
			}));


			this.drawSmoothLine(ctx, points);

			points.forEach(point => {
				ctx.fillStyle = dataset.color;
				ctx.beginPath();
				ctx.arc(point.x, point.y, 5, 0, Math.PI * 2);
				ctx.fill();
			});

			ctx.stroke();
		});
	}

	private drawSmoothLine(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]) {
		if (points.length < 2) return;

		ctx.beginPath();
		ctx.moveTo(points[0].x, points[0].y);

		for (let i = 0; i < points.length - 1; i++) {
			const p0 = i > 0 ? points[i - 1] : points[i];
			const p1 = points[i];
			const p2 = points[i + 1];
			const p3 = i < points.length - 2 ? points[i + 2] : p2;

			const tension = 0.2;
			const cp1x = p1.x + (p2.x - p0.x) * tension;
			const cp1y = p1.y + (p2.y - p0.y) * tension;
			const cp2x = p2.x - (p3.x - p1.x) * tension;
			const cp2y = p2.y - (p3.y - p1.y) * tension;

			ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
		}

		ctx.stroke();
	}

}
